import { useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Container } from '@mui/material';
import { Table } from 'antd';
import dayjs from 'dayjs';
import { getCouponList } from '../../../../api/company';
import AppButton from '../../../../components/ui/AppButton';
import { CouponStatus } from '../../../../constants/coupon';
import { useGetCouponList } from '../../../../hooks/useCompany';
import { handleDownloadExcel } from '../../../../libs/utils';

const columns = [
  {
    title: 'รหัสบริษัท',
    key: 'company_code',
    render: (text, record) => {
      return record?.company_id?.code ?? '-';
    },
  },
  {
    title: 'ชื่อบริษัท',
    key: 'company_name',
    render: (text, record) => {
      return record?.company_id?.company_name ?? '-';
    },
  },
  {
    title: 'คูปอง',
    key: 'code',
    render: (text, record) => {
      return record?.code ?? '-';
    },
  },
  {
    title: 'ชื่อคูปอง',
    key: 'name',
    render: (text, record) => {
      return record?.reward_id?.name ?? '-';
    },
  },
  {
    title: 'มูลค่า',
    key: 'value',
    render: (text, record) => {
      return record?.reward_id?.value ?? '-';
    },
  },
  {
    title: 'สถานะ',
    key: 'status',
    render: (text, record) => {
      if (record?.status.toLowerCase() === CouponStatus.WAITING) {
        return 'ถูกใช้งานแล้ว';
      }

      return 'พร้อมใช้งาน';
    },
  },
  {
    title: 'เวลา',
    key: 'update_timestamp',
    render: (text, record) => {
      if (record?.company_id) {
        return dayjs(record?.update_timestamp).format('DD/MM/YYYY HH:mm:ss') ?? '-';
      }

      return '-';
    },
  },
];

const ManageCouponTab = () => {
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
  });

  const { data: couponList, isPending } = useGetCouponList(query);

  const handleExport = async () => {
    try {
      const response = await getCouponList({ page: 1, limit: 'all' });
      const rows = (response?.Data?.data ?? []).map((item) => ({
        รหัสบริษัท: item?.company_id?.code ?? '',
        ชื่อบริษัท: item?.company_id?.company_name ?? '',
        คูปอง: item?.code ?? '',
        ชื่อคูปอง: item?.reward_id?.name ?? '',
        มูลค่า: item?.reward_id?.value ?? '',
        สถานะ:
          item?.status.toLowerCase() === CouponStatus.WAITING ? 'ถูกใช้งานแล้ว' : 'พร้อมใช้งาน',
        เวลา: item?.company_id ? dayjs(item?.update_timestamp).format('DD-MMM-YYYY HH:mm:ss') : '',
      }));

      handleDownloadExcel(rows, 'coupon_list', `coupon_list__${dayjs().format('DD-MMM-YYYY')}`, {
        header: [
          'รหัสบริษัท',
          'ชื่อบริษัท',
          'ชื่อพนักงานขาย',
          'คูปอง',
          'ชื่อคูปอง',
          'มูลค่า',
          'เวลา',
        ],
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
        minHeight: 150,
        backgroundColor: 'white',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
      }}
    >
      {/* Export button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
        <AppButton variant="danger" sx={{ height: '45px' }} onClick={handleExport}>
          <DownloadIcon sx={{ fontSize: '1rem', display: 'inline-block', marginRight: '5px' }} />
          Export Excel
        </AppButton>
      </Box>

      {/* Table */}
      <Box
        sx={{
          border: '1px solid #eee',
          borderRadius: '10px',
          overflow: 'hidden',
          minHeight: 300,

          '.ant-table-thead > tr': {
            background: 'linear-gradient(90deg,#002B38,#002B38,#002B38,#005D76,#006B7D)',
          },
        }}
      >
        <Table
          columns={columns}
          dataSource={couponList?.Data?.data}
          rowKey={(record) => record?._id}
          pagination={{
            total: couponList?.Data?.total,
            current: query.page,
            pageSize: query.limit,
            showSizeChanger: true,
            pageSizeOptions: ['10', '50', '100', '500'],
            onChange: (page, limit) => setQuery((prev) => ({ ...prev, page, limit })),
          }}
          loading={isPending}
        />
      </Box>
    </Container>
  );
};

export default ManageCouponTab;
